import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import ShippingAddressForm from '@/components/steps/ShippingAddressStep/ShippingAddressForm';
import {
    ADDRESS_TYPE,
    ShippingSchema,
    type ShippingSchemaType,
} from '@/components/steps/ShippingAddressStep/shippingSchema';
import StepBody from '@/components/steps/StepBody';
import { FeatureToggles } from '@/configuration/featureToggle/featureToggles';
import { useFeatureToggle } from '@/hooks/useFeatureToggle';
import { useScrollToTopWhenLoaded } from '@/hooks/useScrollToTopWhenLoaded';

export type { ShippingSchemaType } from '@/components/steps/ShippingAddressStep/shippingSchema';

type ShippingAddressStepProps = {
    state: ShippingSchemaType;
    setState: (state: ShippingSchemaType) => void;
    handleNext: () => void;
    handleBack: () => void;
    isOrderNewCard?: boolean;
};

const ShippingAddressStep = ({
    state,
    setState,
    handleNext,
    handleBack,
    isOrderNewCard = false,
}: ShippingAddressStepProps) => {
    useScrollToTopWhenLoaded(isOrderNewCard);

    const { value: redesignToggleValue } = useFeatureToggle(FeatureToggles.SHIPPING_ADDRESS_REDESIGN);
    const isRedesignEnabled = Boolean(redesignToggleValue);

    const {
        control,
        register,
        handleSubmit,
        setValue,
        clearErrors,
        formState: { errors },
    } = useForm<ShippingSchemaType>({
        defaultValues: {
            ...state,
            addressType: isRedesignEnabled ? (state.addressType ?? ADDRESS_TYPE.company) : state.addressType,
        },
        shouldFocusError: false,
        resolver: zodResolver(ShippingSchema()),
    });

    const onSubmit = (data: ShippingSchemaType) => {
        setState(data);
        handleNext();
    };

    return (
        <StepBody
            stepId='confirmShippingAddress'
            handleNext={handleSubmit(onSubmit)}
            handleBack={handleBack}
            isOrderNewCard={isOrderNewCard}
        >
            <div className={`margin-bottom-15 margin-top-25 rounded bg-white padding-25 ${isOrderNewCard && 'border'}`}>
                <ShippingAddressForm
                    state={state}
                    setValue={setValue}
                    clearErrors={clearErrors}
                    errors={errors}
                    register={register}
                    control={control}
                    isRedesignEnabled={isRedesignEnabled}
                />
            </div>
        </StepBody>
    );
};

export default ShippingAddressStep;
